import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { ResponsiveContainer, ResponsiveHeader, ResponsiveContent, ResponsiveFooter } from '../ui/ResponsiveLayout';
import { PlusCircleIcon, NewDogIcon, NewCatIcon, NewBirdIcon, NewFishIcon, NewReptilesIcon, NewOtherPetsIcon, DocumentsIcon } from '../ui/Icons';
import Modal from '../ui/Modal';
import FileUpload from '../ui/FileUpload';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';

interface Pet {
  id: string;
  name: string;
  type: 'dog' | 'cat' | 'bird' | 'fish' | 'reptile' | 'other' | '';
  breed: string;
  age: string;
  specialNeeds: string;
  veterinarian: string;
  veterinarianContact: string;
  preferredCaregiver: string;
  caregiverContact: string;
  careInstructions: string;
  diet: string;
  medications: string;
  emergencyContact: string;
  emergencyContactPhone: string;
  documents: AppDocument[];
}

interface Dependant {
  id: string;
  name: string;
  relationship: 'child' | 'parent' | 'sibling' | 'spouse' | 'other' | '';
  age: string;
  specialNeeds: string;
  medicalConditions: string;
  medications: string;
  preferredGuardian: string;
  guardianContact: string;
  emergencyContact: string;
  emergencyContactPhone: string;
  careInstructions: string;
  documents: AppDocument[];
}

interface PetsAndDependantsData {
  pets: Pet[];
  dependants: Dependant[];
}

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  return documentService.normalizeMany(docs);
};

const petCategories = [
  { key: 'dog' as const, label: 'Dogs', Icon: NewDogIcon, description: 'Add your dogs and their care instructions' },
  { key: 'cat' as const, label: 'Cats', Icon: NewCatIcon, description: 'Add your cats and their care instructions' },
  { key: 'bird' as const, label: 'Birds', Icon: NewBirdIcon, description: 'Add your birds and their care instructions' },
  { key: 'fish' as const, label: 'Fish', Icon: NewFishIcon, description: 'Add your fish and their care instructions' },
  { key: 'reptile' as const, label: 'Reptiles', Icon: NewReptilesIcon, description: 'Add your reptiles and their care instructions' },
  { key: 'other' as const, label: 'Other\nPets', Icon: NewOtherPetsIcon, description: 'Add any other pets not listed above' }
];

const Frame242: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress } = useSessionPersistence();

  const [data, setData] = useState<PetsAndDependantsData>({ pets: [], dependants: [] });
  const [activeSplit, setActiveSplit] = useState<'pets' | 'dependents'>('pets');

  useEffect(() => {
    if (window.location.pathname.includes('/frame-249')) {
      setActiveSplit('dependents');
    } else {
      setActiveSplit('pets');
    }
    window.scrollTo(0, 0);
  }, [window.location.pathname]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<'pet' | 'dependent'>('pet');
  const [selectedType, setSelectedType] = useState<Pet['type'] | Dependant['relationship']>('');
  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [specialNeeds, setSpecialNeeds] = useState('');
  const [primaryContact, setPrimaryContact] = useState('');
  const [primaryContactPhone, setPrimaryContactPhone] = useState('');
  const [secondaryContact, setSecondaryContact] = useState('');
  const [secondaryContactPhone, setSecondaryContactPhone] = useState('');
  const [medicalInfo, setMedicalInfo] = useState('');
  const [medications, setMedications] = useState('');
  const [careInstructions, setCareInstructions] = useState('');
  const [diet, setDiet] = useState('');
  const [petMedications, setPetMedications] = useState('');
  const [documents, setDocuments] = useState<AppDocument[]>([]);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);

  const normalizePetsAndDependantsData = (input: PetsAndDependantsData): PetsAndDependantsData => {
    return {
      pets: (input.pets || []).map((pet) => ({
        ...pet,
        documents: normalizeDocuments(pet.documents || []),
      })),
      dependants: (input.dependants || []).map((dependant) => ({
        ...dependant,
        documents: normalizeDocuments(dependant.documents || []),
      })),
    };
  };

  const openDocument = (doc: AppDocument) => {
    if (!doc.id) return;
    documentService.open(doc.id);
  };

  const downloadDocument = async (doc: AppDocument) => {
    if (!doc.id) return;

    try {
      await documentService.download(doc.id, doc.name);
    } catch (error) {
      console.error('[Frame242] Failed to download document:', error);
      alert('Failed to download document.');
    }
  };

  // Helper function to save data directly to localStorage
  const saveToLocalStorage = (dataToSave: PetsAndDependantsData) => {
    try {
      const normalized = normalizePetsAndDependantsData(dataToSave);

      const saveData = {
        answers: {
          petsAndDependants: normalized
        },
        lastUpdated: new Date().toISOString()
      };

      localStorage.setItem('pets-dependants-backup', JSON.stringify(normalized));
      localStorage.setItem('pets-dependants-data-backup', JSON.stringify(saveData));

      return true;
    } catch (error) {
      console.error('[Frame242] Direct save failed:', error);
      return false;
    }
  };



  // Load saved data on component mount - PRIORITIZE SESSION FOR CROSS-DEVICE SYNC
  useEffect(() => {
    if (session?.taskProgress?.pets?.answers) {
      const savedData = session.taskProgress.pets.answers.petsAndDependants;
      if (savedData) {
        console.log('[Frame249] Loading from session:', savedData);
        setData(normalizePetsAndDependantsData(savedData));
        return;
      }
    }

    try {
      const backupData = localStorage.getItem('pets-dependants-backup');
      if (backupData) {
        const parsed = JSON.parse(backupData);
        if (parsed) {
          console.log('[Frame249] Loading from localStorage backup:', parsed);
          setData(normalizePetsAndDependantsData(parsed));
        }
      }
    } catch (error) {
      console.error('[Frame249] Failed to load from backup:', error);
    }
  }, [session?.taskProgress?.pets]);

  const updateData = (newData: PetsAndDependantsData) => {
    const normalized = normalizePetsAndDependantsData(newData);
    setData(normalized);

    saveToLocalStorage(normalized);

    updateTaskProgress('pets', { petsAndDependants: normalized }).catch(error => {
      console.error('[Frame249] Session save failed:', error);
    });
  };

  const resetModal = () => {
    setName('');
    setAge('');
    setSpecialNeeds('');
    setPrimaryContact('');
    setPrimaryContactPhone('');
    setSecondaryContact('');
    setSecondaryContactPhone('');
    setMedicalInfo('');
    setMedications('');
    setCareInstructions('');
    setDiet('');
    setPetMedications('');
    setDocuments([]);
    setEditingItemId(null);
    setIsUploadingDocuments(false);
  };

  const openPetModal = (type: Pet['type']) => {
    resetModal();
    setSelectedCategory('pet');
    setSelectedType(type);
    setModalTitle(`Add ${type.charAt(0).toUpperCase() + type.slice(1)}`);
    setModalOpen(true);
  };



  const handleOk = () => {
    if (!name.trim()) {
      alert('Name is required');
      return;
    }

    if (selectedCategory === 'pet') {
      const newPet: Pet = {
        id: editingItemId || Date.now().toString(),
        type: selectedType as Pet['type'],
        name: name.trim(),
        breed: '',
        age: age.trim(),
        specialNeeds: specialNeeds.trim(),
        veterinarian: primaryContact.trim(),
        veterinarianContact: primaryContactPhone.trim(),
        preferredCaregiver: secondaryContact.trim(),
        caregiverContact: secondaryContactPhone.trim(),
        careInstructions: careInstructions.trim(),
        diet: diet.trim(),
        medications: petMedications.trim(),
        emergencyContact: '',
        emergencyContactPhone: '',
        documents: normalizeDocuments(documents)
      };

      const updatedPets = editingItemId
        ? data.pets.map(p => p.id === editingItemId ? newPet : p)
        : [...data.pets, newPet];

      updateData({ ...data, pets: updatedPets });
    } else {
      const newDep: Dependant = {
        id: editingItemId || Date.now().toString(),
        name: name.trim(),
        relationship: selectedType as Dependant['relationship'],
        age: age.trim(),
        specialNeeds: specialNeeds.trim(),
        medicalConditions: medicalInfo.trim(),
        medications: medications.trim(),
        preferredGuardian: primaryContact.trim(),
        guardianContact: primaryContactPhone.trim(),
        emergencyContact: secondaryContact.trim(),
        emergencyContactPhone: secondaryContactPhone.trim(),
        careInstructions: careInstructions.trim(),
        documents: normalizeDocuments(documents)
      };

      const updatedDeps = editingItemId
        ? data.dependants.map(d => d.id === editingItemId ? newDep : d)
        : [...data.dependants, newDep];

      updateData({ ...data, dependants: updatedDeps });
    }

    setModalOpen(false);
  };

  const handleEdit = (item: Pet | Dependant, category: 'pet' | 'dependent') => {
    setSelectedCategory(category);
    setSelectedType(category === 'pet' ? (item as Pet).type : (item as Dependant).relationship);
    setName(item.name);
    setAge(item.age);
    setSpecialNeeds(item.specialNeeds);

    if (category === 'pet') {
      const pet = item as Pet;
      setPrimaryContact(pet.veterinarian);
      setPrimaryContactPhone(pet.veterinarianContact);
      setSecondaryContact(pet.preferredCaregiver);
      setSecondaryContactPhone(pet.caregiverContact);
      setCareInstructions(pet.careInstructions);
      setDiet(pet.diet || '');
      setPetMedications(pet.medications || '');
    } else {
      const dep = item as Dependant;
      setPrimaryContact(dep.preferredGuardian);
      setPrimaryContactPhone(dep.guardianContact);
      setSecondaryContact(dep.emergencyContact);
      setSecondaryContactPhone(dep.emergencyContactPhone);
      setMedicalInfo(dep.medicalConditions);
      setMedications(dep.medications);
      setCareInstructions(dep.careInstructions);
    }

    setDocuments(normalizeDocuments(item.documents || []));
    setEditingItemId(item.id);
    setModalTitle(`Edit ${item.name}`);
    setModalOpen(true);
  };

  const handleDelete = async (id: string, category: 'pet' | 'dependent') => {
    if (!confirm('Are you sure?')) return;

    const itemToDelete =
      category === 'pet'
        ? data.pets.find(p => p.id === id)
        : data.dependants.find(d => d.id === id);

    const attachedDocs = normalizeDocuments(itemToDelete?.documents || []);

    for (const doc of attachedDocs) {
      try {
        await documentService.delete(doc.id);
      } catch (error) {
        console.error('[Frame242] Failed to delete document while deleting item:', error);
      }
    }

    if (category === 'pet') {
      updateData({ ...data, pets: data.pets.filter(p => p.id !== id) });
    } else {
      updateData({ ...data, dependants: data.dependants.filter(d => d.id !== id) });
    }
  };

  const handleRemoveAttachedDocument = async (
    itemId: string,
    category: 'pet' | 'dependent',
    documentIndex: number
  ) => {
    const item =
      category === 'pet'
        ? data.pets.find(p => p.id === itemId)
        : data.dependants.find(d => d.id === itemId);

    const docToRemove = item?.documents?.[documentIndex];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame242] Failed to delete saved document:', error);
      alert('Failed to delete document.');
      return;
    }

    if (category === 'pet') {
      const updatedPets = data.pets.map((pet) => {
        if (pet.id !== itemId) return pet;
        return {
          ...pet,
          documents: normalizeDocuments(pet.documents || []).filter((_, index) => index !== documentIndex),
        };
      });
      updateData({ ...data, pets: updatedPets });
    } else {
      const updatedDependants = data.dependants.map((dependant) => {
        if (dependant.id !== itemId) return dependant;
        return {
          ...dependant,
          documents: normalizeDocuments(dependant.documents || []).filter((_, index) => index !== documentIndex),
        };
      });
      updateData({ ...data, dependants: updatedDependants });
    }
  };

  const removeDocumentFromUploadModal = async (index: number) => {
    const docToRemove = documents[index];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame242] Failed to delete unsaved document:', error);
      alert('Failed to delete document.');
      return;
    }

    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleNext = async () => {
    try {
      if (!saveToLocalStorage(data)) {
        throw new Error('Failed to save to local storage');
      }
      await updateTaskProgress('pets', { petsAndDependants: normalizePetsAndDependantsData(data) });
      navigate('/frame-249');
    } catch (error) {
      console.error('[Frame242] Save failed:', error);
      const message = 'Failed to save your changes. Please try again.';
      if (confirm(message + '\n\nClick OK to try again or Cancel to discard changes.')) {
        handleNext();
      } else {
        navigate('/frame-249');
      }
    }
  };

  const handleBack = async () => {
    await updateTaskProgress('pets', { petsAndDependants: normalizePetsAndDependantsData(data) });
    navigate(activeSplit === 'dependents' ? '/frame-242' : '/frame-241');
  };

  const handleCancel = () => {
    setModalOpen(false);
    resetModal();
  };

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader
          title="Add Your Pets"
        />

        <div className="max-w-4xl">
          <div className="text-stone-800 text-lg font-medium font-sans mb-8 leading-relaxed">
            {activeSplit === 'pets'
              ? "Add any pets that depend on you for care. You'll provide additional details in the next steps."
              : "Add any dependents who rely on you for care."}
          </div>

          <div className="mb-12">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {petCategories.map(category => (
                <button
                  key={category.key}
                  onClick={() => openPetModal(category.key)}
                  className="group w-full bg-white rounded-lg border border-neutral-200 hover:border-cyan-500 transition-colors text-left p-6"
                >
                  <div className="flex items-center gap-4">
                    <category.Icon className="w-8 h-8 text-cyan-600 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-stone-800 leading-tight">{category.label.replace('\n', ' ')}</h3>
                      <p className="text-sm text-stone-600 mt-1">{category.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Pet List */}
            {data.pets.length > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-medium text-stone-800 mb-4">Your Pets ({data.pets.length})</h3>
                <div className={`space-y-4 ${data.pets.length >= 3 ? 'max-h-56 min-h-0 overflow-y-auto' : ''}`}>
                  {data.pets.map(pet => {
                    const PetIcon = petCategories.find(c => c.key === pet.type)?.Icon || PlusCircleIcon;
                    const attachedDocs = normalizeDocuments(pet.documents || []);

                    return (
                      <div
                        key={pet.id}
                        className="bg-white rounded-lg border border-neutral-200 p-4"
                      >
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                          <div className="flex flex-col gap-2 min-w-0 flex-1 sm:flex-row sm:items-start sm:gap-4">
                            <PetIcon className="w-6 h-6 text-cyan-700 flex-shrink-0" />

                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-stone-800 break-words">
                                {pet.name}
                              </div>

                              <div className="text-sm text-stone-500 mt-1 break-words">
                                {[
                                  pet.type.charAt(0).toUpperCase() + pet.type.slice(1),
                                  pet.age,
                                  pet.veterinarian && `Vet: ${pet.veterinarian}`,
                                  pet.preferredCaregiver && `Caregiver: ${pet.preferredCaregiver}`
                                ].filter(Boolean).join(' • ')}
                              </div>

                              {pet.specialNeeds && (
                                <div className="text-sm text-stone-600 mt-2 break-words">
                                  {pet.specialNeeds}
                                </div>
                              )}

                              {pet.careInstructions && (
                                <div className="text-sm text-stone-600 mt-2 whitespace-pre-wrap break-words">
                                  {pet.careInstructions}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2 sm:flex-nowrap sm:shrink-0">
                            <button
                              onClick={() => handleEdit(pet, 'pet')}
                              className="text-cyan-600 hover:text-cyan-700 px-3 py-1 rounded border border-cyan-200 hover:border-cyan-300"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(pet.id, 'pet')}
                              className="text-red-600 hover:text-red-700 px-3 py-1 rounded border border-red-200 hover:border-red-300"
                            >
                              Delete
                            </button>
                          </div>
                        </div>

                        {attachedDocs.length > 0 && (
                          <div className="mt-4 space-y-2">
                            {attachedDocs.map((doc, docIndex) => (
                              <div
                                key={`${pet.id}-${doc.id}-${docIndex}`}
                                className="flex flex-col gap-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                              >
                                <button
                                  type="button"
                                  onClick={() => openDocument(doc)}
                                  className="flex items-start gap-2 min-w-0 w-full text-left sm:flex-1"
                                >
                                  <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                                  <span
                                    className="text-sm font-medium text-stone-800 truncate"
                                    title={doc.name}
                                  >
                                    {doc.name}
                                  </span>
                                </button>

                                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => downloadDocument(doc)}
                                    className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                                  >
                                    Download
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => handleRemoveAttachedDocument(pet.id, 'pet', docIndex)}
                                    className="text-red-500 hover:text-red-700 text-xs shrink-0"
                                    title="Remove document"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </ResponsiveContent>

      {/* Add/Edit Modal */}
      <Modal
        title={modalTitle}
        open={modalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={editingItemId ? 'Save Changes' : 'Save'}
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-cyan-500" placeholder="Name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
            <input type="text" value={age} onChange={(e) => setAge(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-cyan-500" placeholder="Age" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Special Needs</label>
            <textarea value={specialNeeds} onChange={(e) => setSpecialNeeds(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-cyan-500" rows={3} placeholder="Special needs" />
          </div>

          {selectedCategory === 'dependent' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Medical Conditions</label>
                <textarea value={medicalInfo} onChange={(e) => setMedicalInfo(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-cyan-500" rows={3} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Medications</label>
                <textarea value={medications} onChange={(e) => setMedications(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-cyan-500" rows={3} />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{selectedCategory === 'pet' ? 'Veterinarian' : 'Guardian'}</label>
            <input type="text" value={primaryContact} onChange={(e) => setPrimaryContact(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-cyan-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
            <input type="tel" value={primaryContactPhone} onChange={(e) => setPrimaryContactPhone(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-cyan-500" />
          </div>

          {selectedCategory === 'pet' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Diet</label>
                <textarea value={diet} onChange={(e) => setDiet(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-cyan-500" rows={3} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pet Medications</label>
                <textarea value={petMedications} onChange={(e) => setPetMedications(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-cyan-500" rows={3} />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Care Instructions</label>
            <textarea value={careInstructions} onChange={(e) => setCareInstructions(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-cyan-500" rows={3} />
          </div>

          {/* Documents Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Documents
            </label>
            <FileUpload
              onUpload={async (files: File[]) => {
                if (!files.length) return;

                setIsUploadingDocuments(true);

                try {
                  const uploadedDocs = await Promise.all(
                    files.map((file) => documentService.upload(file))
                  );
                  setDocuments((prev) => [...prev, ...uploadedDocs]);
                } catch (error) {
                  console.error('[Frame242] Failed to upload document(s):', error);
                  alert('Failed to upload one or more documents.');
                } finally {
                  setIsUploadingDocuments(false);
                }
              }}
              accept=".pdf,.jpg,.png,.docx"
              multiple
            />

            {isUploadingDocuments && (
              <div className="mt-3 text-sm text-stone-500">
                Uploading documents...
              </div>
            )}

            {documents.length > 0 && (
              <div className="space-y-2 mt-4">
                {documents.map((doc, index) => (
                  <div
                    key={`${doc.id}-${index}`}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-white border border-gray-200 rounded-md gap-3"
                  >
                    <button
                      type="button"
                      onClick={() => openDocument(doc)}
                      className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-left"
                    >
                      <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700 truncate font-medium" title={doc.name}>
                        {doc.name}
                      </span>
                    </button>

                    <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                      <button
                        type="button"
                        onClick={() => downloadDocument(doc)}
                        className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                      >
                        Download
                      </button>
                      <button
                        type="button"
                        onClick={() => removeDocumentFromUploadModal(index)}
                        className="text-red-500 hover:text-red-700 text-sm font-semibold ml-4"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>

      <ResponsiveFooter
        title="Plan for Your Pets and Dependents"
        subtitle="Add any pets that depend on you for care"
        progressPercent={20}
        onBack={handleBack}
        onNext={handleNext}
        nextText="Next"
        backText="Back"
      />
    </ResponsiveContainer>
  );
};

export default Frame242;